import * as React from "react";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import MailOutlineRoundedIcon    from "@mui/icons-material/MailOutlineRounded";
import PhoneForwardedOutlinedIcon from "@mui/icons-material/PhoneForwardedOutlined";
import ChatBubbleOutlineIcon     from "@mui/icons-material/ChatBubbleOutline";
import AccessTimeIcon            from "@mui/icons-material/AccessTime";

const channels = [
  {
    Icon:    PhoneForwardedOutlinedIcon,
    accent:  "#FF6B35",
    title:   "Call us",
    body:    "Talk to our team directly. Available Monday to Saturday, 10 AM – 7 PM IST.",
    cta:     "tel:+918142051271",
    ctaLabel: "+91 81420 51271",
    ctaExternal: false,
  },
  {
    Icon:    MailOutlineRoundedIcon,
    accent:  "#22C55E",
    title:   "Email us",
    body:    "Drop us a line for any query — billing, partnerships, or just feedback. We reply within 24 hours.",
    cta:     "mailto:support@meesha.co.in",
    ctaLabel: "support@meesha.co.in",
    ctaExternal: false,
  },
  {
    Icon:    ChatBubbleOutlineIcon,
    accent:  "#4A90E2",
    title:   "Send a query",
    body:    "Fill out our quick contact form and our team will get back to you with the right answer.",
    cta:     "https://forms.gle/7PGr6zwphBvWC3HT9",
    ctaLabel: "Open contact form →",
    ctaExternal: true,
  },
];

const faqs = [
  {
    q: "How quickly can I find a creator?",
    a: "Meesha's search returns results in under 60 seconds. You can filter by niche, follower count, engagement rate, location, and audience authenticity score.",
  },
  {
    q: "Are the contact details verified?",
    a: "Yes — every phone number and email address on Meesha is actively validated, not just scraped. Our contact reach rate is 95%.",
  },
  {
    q: "Do I need to sign a long-term contract?",
    a: "No. All plans are flexible. You can upgrade, downgrade, or cancel at any time. No lock-ins, no hidden fees.",
  },
  {
    q: "Can I get a demo before subscribing?",
    a: "Absolutely. Reach out via call or email and our team will walk you through the platform live, tailored to your use case.",
  },
];

function ContactDetails() {
  return (
    <Box sx={{ bgcolor: "#060F24", minHeight: "100vh" }}>

      {/* ── Hero header ── */}
      <Box sx={{
        bgcolor:   "#0D1B3E",
        pt:        { xs: "120px", md: "160px" },
        pb:        { xs: "64px",  md: "90px"  },
        px:        { xs: "20px",  md: "80px"  },
        textAlign: "center",
        position:  "relative",
        overflow:  "hidden",
      }}>
        <Box sx={{
          position:   "absolute",
          width:      500,
          height:     500,
          top:        "-120px",
          left:       "50%",
          transform:  "translateX(-50%)",
          background: "radial-gradient(circle, rgba(255,107,53,0.07), transparent 65%)",
          filter:     "blur(70px)",
          pointerEvents: "none",
        }} />

        <Box component="span" sx={{
          display:       "inline-block",
          color:         "#FF6B35",
          bgcolor:       "rgba(255,107,53,0.1)",
          border:        "1px solid rgba(255,107,53,0.3)",
          px:            "16px",
          py:            "6px",
          borderRadius:  "100px",
          fontSize:      "11px",
          fontWeight:    700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          mb:            3,
        }}>
          Get in touch
        </Box>

        <Typography component="h1" sx={{
          fontSize:   "clamp(30px, 4vw, 52px)",
          fontWeight: 800,
          color:      "white",
          lineHeight: 1.15,
          maxWidth:   "640px",
          mx:         "auto",
          mb:         2.5,
        }}>
          We'd love to{" "}
          <Box component="span" sx={{
            background:           "linear-gradient(90deg, #FF6B35 0%, #FF9F6B 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor:  "transparent",
            backgroundClip:       "text",
          }}>
            hear from you.
          </Box>
        </Typography>

        <Typography sx={{
          fontSize:  "16px",
          color:     "rgba(255,255,255,0.5)",
          maxWidth:  "500px",
          mx:        "auto",
          lineHeight: 1.75,
        }}>
          Whether you have a question, need a demo, or want to explore a partnership — our team is here and responsive.
        </Typography>

        {/* Response time pill */}
        <Box sx={{
          display:       "inline-flex",
          alignItems:    "center",
          gap:           1,
          mt:            4,
          bgcolor:       "rgba(34,197,94,0.1)",
          border:        "1px solid rgba(34,197,94,0.25)",
          borderRadius:  "100px",
          px:            "18px",
          py:            "9px",
        }}>
          <AccessTimeIcon sx={{ fontSize: 15, color: "#22C55E" }} />
          <Typography sx={{ fontSize: "13px", color: "#22C55E", fontWeight: 600 }}>
            We typically reply within 24 hours
          </Typography>
        </Box>
      </Box>

      {/* ── Contact channels ── */}
      <Container maxWidth="lg" sx={{ py: { xs: "64px", md: "96px" }, px: { xs: "20px", md: "40px" } }}>
        <Grid container spacing={3}>
          {channels.map((ch, i) => (
            <Grid key={i} item xs={12} md={4}>
              <Box sx={{
                height:       "100%",
                bgcolor:      "rgba(255,255,255,0.03)",
                border:       "1px solid rgba(255,255,255,0.07)",
                borderRadius: "20px",
                p:            "32px 28px",
                display:      "flex",
                flexDirection: "column",
                position:     "relative",
                overflow:     "hidden",
                transition:   "all 0.3s ease",
                "&:hover": {
                  bgcolor:     "rgba(255,255,255,0.055)",
                  borderColor: `${ch.accent}44`,
                  transform:   "translateY(-6px)",
                  boxShadow:   `0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px ${ch.accent}22`,
                },
              }}>
                {/* Corner glow */}
                <Box sx={{
                  position:     "absolute",
                  top:          -40,
                  right:        -40,
                  width:        120,
                  height:       120,
                  borderRadius: "50%",
                  background:   `radial-gradient(circle, ${ch.accent}18, transparent 70%)`,
                  pointerEvents: "none",
                }} />

                {/* Icon */}
                <Box sx={{
                  width:          48,
                  height:         48,
                  borderRadius:   "14px",
                  bgcolor:        `${ch.accent}15`,
                  border:         `1px solid ${ch.accent}30`,
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  mb:             2.5,
                }}>
                  <ch.Icon sx={{ color: ch.accent, fontSize: 22 }} />
                </Box>

                <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "white", mb: 1.5, lineHeight: 1.3 }}>
                  {ch.title}
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "rgba(255,255,255,0.5)", lineHeight: 1.75, mb: 3, flexGrow: 1 }}>
                  {ch.body}
                </Typography>

                <Button
                  href={ch.cta}
                  target={ch.ctaExternal ? "_blank" : undefined}
                  rel={ch.ctaExternal ? "noopener noreferrer" : undefined}
                  sx={{
                    alignSelf:     "flex-start",
                    bgcolor:       `${ch.accent}15`,
                    color:         ch.accent,
                    border:        `1px solid ${ch.accent}30`,
                    fontWeight:    700,
                    fontSize:      "13px",
                    px:            "18px",
                    py:            "9px",
                    borderRadius:  "10px",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor:     `${ch.accent}25`,
                      borderColor: `${ch.accent}60`,
                    },
                    transition:    "all 0.2s ease",
                  }}
                >
                  {ch.ctaLabel}
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── FAQ ── */}
      <Box sx={{
        bgcolor:   "#0a1630",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        py:        { xs: "64px", md: "96px" },
        px:        { xs: "20px", md: "80px" },
      }}>
        <Box sx={{ textAlign: "center", mb: { xs: "40px", md: "60px" } }}>
          <Box component="span" sx={{
            display:       "inline-block",
            color:         "#FF6B35",
            bgcolor:       "rgba(255,107,53,0.1)",
            border:        "1px solid rgba(255,107,53,0.25)",
            px:            "14px",
            py:            "5px",
            borderRadius:  "100px",
            fontSize:      "10px",
            fontWeight:    700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            mb:            2.5,
          }}>
            Common questions
          </Box>
          <Typography sx={{
            fontSize:   "clamp(22px, 2.8vw, 34px)",
            fontWeight: 800,
            color:      "white",
            lineHeight: 1.25,
          }}>
            You might already have the answer
          </Typography>
        </Box>

        <Container maxWidth="md" disableGutters>
          <Grid container spacing={2.5}>
            {faqs.map((faq, i) => (
              <Grid key={i} item xs={12} md={6}>
                <Box sx={{
                  bgcolor:      "rgba(255,255,255,0.03)",
                  border:       "1px solid rgba(255,255,255,0.07)",
                  borderRadius: "16px",
                  p:            "24px",
                  height:       "100%",
                  transition:   "border-color 0.25s ease",
                  "&:hover": { borderColor: "rgba(255,107,53,0.25)" },
                }}>
                  <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "white", mb: 1.5, lineHeight: 1.4 }}>
                    {faq.q}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: "rgba(255,255,255,0.5)", lineHeight: 1.75 }}>
                    {faq.a}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

    </Box>
  );
}

export default ContactDetails;
